import { useMemo, useState } from 'react';
import { useOverview, useProductionVessels, useServiceSheet, type SheetGridRow } from '../features/hooks.js';
import { ActionBar, EmptyState, ErrorState, LoadingState, PageHeader } from '../components/ui.js';
import { useIsCoarse, useIsPhone } from '../lib/media.js';
import { fold, hasText } from '../lib/search.js';

type Source = 'production' | 'overview' | 'service';

interface LabelItem {
  key: string; // unique selection id
  sap: string; // big identifier ('' for Service, which has no SAP)
  name: string;
  model: string; // model, or (Service) the group/section name
  hasAnf: boolean;
  extras: boolean; // show the AnF badge + FUEL + Start-Up footer (never on Service)
}

const cellStr = (v: string | boolean | undefined) => (typeof v === 'string' ? v : v == null ? '' : String(v));

// hand-tick checkboxes printed on each sticker's footer (production / overview labels only).
const STICKER_CHECKS = ['FUEL', 'Start-Up', 'Tank Venting'] as const;

export function Labels() {
  const prod = useProductionVessels();
  const overview = useOverview();
  const service = useServiceSheet();

  const [source, setSource] = useState<Source>('production');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [inProdOnly, setInProdOnly] = useState(true);
  const isPhone = useIsPhone();
  const coarse = useIsCoarse();

  // build a uniform label list from whichever source is active.
  const items = useMemo<LabelItem[]>(() => {
    if (source === 'overview') {
      // every vessel on the overview master grid — AnF comes straight off the row.
      return (overview.data?.vessels ?? [])
        .filter((v) => v.name)
        .map((v) => ({
          key: `ov-${v.rowId}`,
          sap: String(v.sap ?? ''),
          name: v.name,
          model: v.model,
          hasAnf: v.anf === 'Yes' || v.anf === 'Hold',
          extras: true,
        }));
    }
    if (source === 'service') {
      // service sheet hierarchy: top-level rows are GROUPS; their children are the vessels.
      const cols = service.data?.columns ?? [];
      const primary = cols.find((c) => c.primary) ?? cols[0];
      const kidsOf = new Map<number | null, SheetGridRow[]>();
      for (const r of service.data?.rows ?? []) {
        const p = r.parentId ?? null;
        (kidsOf.get(p) ?? kidsOf.set(p, []).get(p)!).push(r);
      }
      const nameOf = (r: SheetGridRow) => (primary ? cellStr(r.cells[primary.id]).trim() : '');
      const out: LabelItem[] = [];
      for (const g of kidsOf.get(null) ?? []) {
        const vessels = kidsOf.get(g.id) ?? [];
        if (!vessels.length) continue; // skip legend / spacer top-level rows (they have no children)
        const groupName = nameOf(g);
        for (const v of vessels) {
          const nm = nameOf(v);
          if (!nm) continue; // skip blank vessel rows
          // service labels: no SAP, no AnF / FUEL / Start-Up.
          out.push({ key: `svc-${v.id}`, sap: '', name: nm, model: groupName, hasAnf: false, extras: false });
        }
      }
      return out;
    }
    // production (default) ; AnF is looked up from the overview by SAP.
    const anfBySap = new Map((overview.data?.vessels ?? []).map((v) => [String(v.sap), v.anf]));
    let all = (prod.data?.vessels ?? []).filter((v) => v.name);
    if (inProdOnly) all = all.filter((v) => v.remaining >= 2);
    return all.map((v) => {
      const a = anfBySap.get(String(v.sap));
      return {
        key: `pr-${v.sap}`,
        sap: String(v.sap),
        name: v.name,
        model: v.model,
        hasAnf: a === 'Yes' || a === 'Hold',
        extras: true,
      };
    });
  }, [source, prod.data, overview.data, service.data, inProdOnly]);

  const filtered = useMemo(() => {
    const query = fold(q.trim());
    if (!query) return items;
    return items.filter((v) => hasText(query, v.name, v.sap, v.model));
  }, [items, q]);

  const toggle = (key: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  // selection keys are source-specific, so reset the selection when the source changes.
  const changeSource = (next: Source) => {
    setSource(next);
    setSelected(new Set());
  };

  const chosen = filtered.filter((v) => selected.has(v.key));
  const active = source === 'overview' ? overview : source === 'service' ? service : prod;

  return (
    <>
      <PageHeader title="Labels">
        <select className="field" value={source} onChange={(e) => changeSource(e.target.value as Source)} title="Label source">
          <option value="production">In production</option>
          <option value="overview">All overview</option>
          <option value="service">Service</option>
        </select>
        <input className="field" placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} />
        {source === 'production' && (
          <label className={coarse ? 'muted check-row' : 'muted'}><input type="checkbox" checked={inProdOnly} onChange={(e) => setInProdOnly(e.target.checked)} /> In production</label>
        )}
        <button className="btn btn-sm" onClick={() => setSelected(new Set(filtered.map((v) => v.key)))}>Select all</button>
        {/* phones: Clear and Print ride in the bottom bar, in reach after picking down a long list */}
        {!isPhone && (
          <>
            <button className="btn btn-sm btn-ghost" onClick={() => setSelected(new Set())}>Clear</button>
            <button className="btn btn-sm btn-primary" disabled={chosen.length === 0} onClick={() => window.print()}>
              Print ({chosen.length})
            </button>
          </>
        )}
      </PageHeader>

      {active.isLoading && <LoadingState />}
      {active.error && <ErrorState error={active.error} onRetry={() => void active.refetch()} />}

      {active.data && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
          <div className="page-panel no-print lbl-panel">
            {filtered.map((v) => (
              <label key={v.key} className="lbl-pick">
                <input type="checkbox" checked={selected.has(v.key)} onChange={() => toggle(v.key)} />
                <span>{v.model ? `${v.model} · ` : ''}{v.name}</span>
                {v.sap && <span className="muted" style={{ marginLeft: 'auto' }}>{v.sap}</span>}
              </label>
            ))}
            {filtered.length === 0 && <div className="muted" style={{ padding: 8 }}>No vessels.</div>}
          </div>

          {/* doc-print-area: the shared print isolation (everything else is hidden when printing) */}
          <div className="print-area doc-print-area" style={{ flex: '1 1 260px', minWidth: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 10 }}>
            {chosen.length === 0 && <EmptyState title="No labels selected" hint="Pick vessels from the list." />}
            {chosen.map((v) => (
              <div key={v.key} style={{ border: '1px solid #000', borderRadius: 4, padding: 14, background: '#fff', color: '#000' }}>
                <div style={{ fontFamily: 'var(--font-disp)', fontSize: 22, fontWeight: 700 }}>{v.sap || v.name}</div>
                {v.sap && <div style={{ fontSize: 16 }}>{v.name}</div>}
                {v.model && <div style={{ color: '#555' }}>{v.model}</div>}
                {v.extras && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, borderTop: '1px solid #ddd', paddingTop: 8 }}>
                    {v.hasAnf && (
                      <span style={{ border: '1.5px solid #000', borderRadius: 3, padding: '1px 7px', fontWeight: 700, fontSize: 13, letterSpacing: 0.5 }}>AnF</span>
                    )}
                    <span style={{ display: 'flex', alignItems: 'center', gap: 12, marginLeft: 'auto', fontSize: 13, fontWeight: 600, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      {STICKER_CHECKS.map((lbl) => (
                        <span key={lbl} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span aria-hidden style={{ display: 'inline-block', width: 15, height: 15, border: '1.5px solid #000', borderRadius: 2 }} />
                          {lbl}
                        </span>
                      ))}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      <ActionBar show={isPhone && chosen.length > 0} className="no-print">
        <button className="btn btn-ghost" onClick={() => setSelected(new Set())}>Clear</button>
        <button className="btn btn-primary lbl-print" onClick={() => window.print()}>Print ({chosen.length})</button>
      </ActionBar>
    </>
  );
}
